export type TipoPatrocinadorLocal = 'negocio-local' | 'empresa' | 'evento' | 'restaurante' | 'tienda' | 'servicio';

export type DisplayPatrocinadorLocal = 'radio' | 'programa' | 'evento' | 'concurso' | 'club';

export type EstadoPatrocinio = 'activo' | 'pendiente' | 'finalizado' | 'renovacion';

export interface MetricaPatrocinador {
  impresiones: number;
  clicks: number;
  leads: number;
  alcance: number;
  ctr: number;
  conversionRate: number;
  periodo: string;
}

export interface PatrocinadorLocal {
  id: number;
  nombreNegocio: string;
  tipo: TipoPatrocinadorLocal;
  logoUrl: string;
  imagenUrl: string;
  ciudad: string;
  estado: string;
  pais: string;
  direccion: string;
  descripcion: string;
  sector: string;
  contactoNombre: string;
  contactoEmail: string;
  contactoTelefono: string;
  website: string;
  fechaRegistro: string;
  radioNombre: string;
  radioFrecuencia: string;
  radioLogoUrl: string;
  displayEn: DisplayPatrocinadorLocal[];
  estado: EstadoPatrocinio;
  inversionTotal: number;
  inversionMensual: number;
  duracionMeses: number;
  fechaInicio: string;
  fechaFin: string;
  metricas: MetricaPatrocinador;
  beneficios: string[];
  categoriaRadio: string;
  tags: string[];
}

export const tiposPatrocinadorLocal: { id: TipoPatrocinadorLocal; label: string; icono: string; color: string; bg: string; descripcion: string; }[] = [
  { id: 'negocio-local', label: 'Negocio Local', icono: 'ri-store-2-line', color: 'text-emerald-600', bg: 'bg-emerald-50', descripcion: 'Comercios de barrio que patrocinan radios de su zona' },
  { id: 'restaurante', label: 'Restaurante', icono: 'ri-restaurant-line', color: 'text-amber-600', bg: 'bg-amber-50', descripcion: 'Restaurantes, cafeterías y bares con presencia radial local' },
  { id: 'tienda', label: 'Tienda', icono: 'ri-shopping-bag-3-line', color: 'text-rose-600', bg: 'bg-rose-50', descripcion: 'Tiendas minoristas, boutiques y comercios especializados' },
  { id: 'servicio', label: 'Servicio', icono: 'ri-tools-line', color: 'text-sky-600', bg: 'bg-sky-50', descripcion: 'Servicios profesionales: salud, belleza, reparaciones, consultoría' },
  { id: 'empresa', label: 'Empresa', icono: 'ri-building-2-line', color: 'text-violet-600', bg: 'bg-violet-50', descripcion: 'Empresas medianas con estrategia de marketing regional' },
  { id: 'evento', label: 'Evento', icono: 'ri-calendar-event-line', color: 'text-orange-600', bg: 'bg-orange-50', descripcion: 'Eventos locales, festivales y ferias que buscan difusión radial' },
];

export const displaysPatrocinadorLocal: { id: DisplayPatrocinadorLocal; label: string; icono: string; color: string; bg: string; descripcion: string; }[] = [
  { id: 'radio', label: 'Radio', icono: 'ri-radio-line', color: 'text-rose-600', bg: 'bg-rose-50', descripcion: 'Visible en la página principal de la radio' },
  { id: 'programa', label: 'Programa', icono: 'ri-mic-line', color: 'text-emerald-600', bg: 'bg-emerald-50', descripcion: 'Aparece durante programas específicos' },
  { id: 'evento', label: 'Evento', icono: 'ri-calendar-event-line', color: 'text-amber-600', bg: 'bg-amber-50', descripcion: 'Patrocina eventos organizados por la radio' },
  { id: 'concurso', label: 'Concurso', icono: 'ri-trophy-line', color: 'text-violet-600', bg: 'bg-violet-50', descripcion: 'Sponsor de concursos y sorteos' },
  { id: 'club', label: 'Club', icono: 'ri-group-line', color: 'text-sky-600', bg: 'bg-sky-50', descripcion: 'Patrocinador del club de fans de la radio' },
];

export const metricasGlobalesPatrocinadoresLocales = {
  totalPatrocinadores: 36,
  patrocinadoresActivos: 28,
  radiosConPatrocinio: 15,
  inversionTotal: 847500,
  inversionPromedioMensual: 23500,
  impresionesTotales: 4850000,
  clicksTotales: 742000,
  leadsTotales: 89300,
  alcanceTotal: 12400000,
  ctrPromedio: 15.3,
  conversionPromedio: 12.1,
  tiposActivos: { 'negocio-local': 9, 'restaurante': 7, 'tienda': 5, 'servicio': 6, 'empresa': 5, 'evento': 4 },
  displaysActivos: { 'radio': 22, 'programa': 18, 'evento': 12, 'concurso': 10, 'club': 8 },
};

export const reglasSistemaPatrocinadoresLocales = [
  {
    id: 1,
    titulo: 'La Radio Administra sus Propios Patrocinadores',
    descripcion: 'Cada radio gestiona sus patrocinadores locales de forma independiente. Negocia precios, duración, ubicación de display y beneficios directamente con el negocio. RFW no interviene en la transacción comercial.',
    icono: 'ri-radio-line',
    gradiente: 'from-rose-500/10 to-amber-500/10',
    borde: 'border-rose-200/60',
    colorTexto: 'text-rose-700',
  },
  {
    id: 2,
    titulo: 'Monetización Regional, Impacto Local',
    descripcion: 'El sistema está diseñado para que negocios de barrio patrocinen radios de su zona. La cercanía geográfica asegura relevancia: una ferretería en CDMX patrocina una radio que escuchan en CDMX.',
    icono: 'ri-map-pin-line',
    gradiente: 'from-emerald-500/10 to-teal-500/10',
    borde: 'border-emerald-200/60',
    colorTexto: 'text-emerald-700',
  },
  {
    id: 3,
    titulo: 'Métricas Transparentes en Tiempo Real',
    descripcion: 'Cada patrocinador recibe un dashboard con impresiones, clicks, leads y alcance. Las métricas son auditables y se actualizan diariamente. El negocio sabe exactamente qué retorno obtiene.',
    icono: 'ri-bar-chart-line',
    gradiente: 'from-violet-500/10 to-purple-500/10',
    borde: 'border-violet-200/60',
    colorTexto: 'text-violet-700',
  },
  {
    id: 4,
    titulo: 'Seis Tipos de Negocio, Cinco Ubicaciones de Display',
    descripcion: 'Negocio Local, Restaurante, Tienda, Servicio, Empresa y Evento pueden aparecer en Radio, Programa, Evento, Concurso y Club. La radio elige dónde y cómo mostrar cada patrocinador.',
    icono: 'ri-layout-grid-line',
    gradiente: 'from-amber-500/10 to-orange-500/10',
    borde: 'border-amber-200/60',
    colorTexto: 'text-amber-700',
  },
];

export const patrocinadoresLocales: PatrocinadorLocal[] = [
  {
    id: 1,
    nombreNegocio: 'Ferretería El Martillo',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20hardware%20store%20brand%20logo%20design%20with%20abstract%20hammer%20and%20nail%20geometric%20shapes%2C%20warm%20rust%20orange%20and%20steel%20gray%20tones%2C%20modern%20industrial%20style%2C%20dark%20background%2C%20friendly%20neighborhood%20feel%2C%20clean%20simple%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-01&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Warm%20welcoming%20neighborhood%20hardware%20store%20interior%20with%20tools%20and%20paint%20cans%20on%20wooden%20shelves%2C%20soft%20natural%20lighting%20through%20storefront%20windows%2C%20rustic%20industrial%20aesthetic%2C%20community%20feel%2C%20editorial%20photography%20with%20warm%20amber%20tones%20and%20clean%20composition&width=800&height=450&seq=pat-local-img-01&orientation=landscape',
    ciudad: 'Ciudad de México',
    estado: 'CDMX',
    pais: 'México',
    direccion: 'Av. Insurgentes 2450, Col. Roma Norte',
    descripcion: 'Ferretería de barrio con 35 años de historia en la Roma Norte. Especialistas en plomería, electricidad y pintura. Atendido por sus dueños.',
    sector: 'Ferretería y Construcción',
    contactoNombre: 'Don Manuel Ortega',
    contactoEmail: 'manuel@ferreteriaelmartillo.mx',
    contactoTelefono: '+52 55 1234 5678',
    website: 'https://ferreteriaelmartillo.mx',
    fechaRegistro: '2026-01-15',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '104.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20mexican%20folk%20art%20inspired%20geometric%20pattern%2C%20warm%20terracotta%20and%20gold%20tones%2C%20circular%20emblem%20style%2C%20dark%20background%2C%20vibrant%20cultural%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-mexicana&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 18000,
    inversionMensual: 3000,
    duracionMeses: 6,
    fechaInicio: '2026-02-01',
    fechaFin: '2026-08-01',
    metricas: { impresiones: 124000, clicks: 18500, leads: 1240, alcance: 320000, ctr: 14.9, conversionRate: 6.7, periodo: 'Feb-Ago 2026' },
    beneficios: ['Mención en vivo 3 veces por programa', 'Logo en página de la radio', 'Cupón de descuento exclusivo para oyentes'],
    categoriaRadio: 'Regional Mexicana',
    tags: ['ferreteria', 'cdmx', 'barrio', 'construccion', 'local'],
  },
  {
    id: 2,
    nombreNegocio: 'Taquería Los Gueros',
    tipo: 'restaurante',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20taco%20restaurant%20brand%20logo%20with%20abstract%20taco%20and%20chili%20pepper%20geometric%20design%2C%20warm%20orange%20and%20lime%20green%20tones%2C%20modern%20casual%20dining%20style%2C%20dark%20background%2C%20appetizing%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-02&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Authentic%20mexican%20taqueria%20interior%20with%20warm%20ambient%20lighting%2C%20colorful%20tiled%20walls%2C%20fresh%20ingredients%20on%20display%2C%20cozy%20neighborhood%20restaurant%20atmosphere%2C%20steam%20rising%20from%20grill%2C%20editorial%20food%20photography%20with%20rich%20warm%20tones%20and%20organic%20composition&width=800&height=450&seq=pat-local-img-02&orientation=landscape',
    ciudad: 'Ciudad de México',
    estado: 'CDMX',
    pais: 'México',
    direccion: 'Calle Durango 312, Col. Condesa',
    descripcion: 'Taquería legendaria en la Condesa desde 1988. Famosos por sus tacos al pastor y su salsa secreta. Punto de encuentro del barrio.',
    sector: 'Gastronomía',
    contactoNombre: 'Familia Guero Hernández',
    contactoEmail: 'hola@taquerialosgueros.mx',
    contactoTelefono: '+52 55 2345 6789',
    website: 'https://taquerialosgueros.mx',
    fechaRegistro: '2025-11-20',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '104.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20mexican%20folk%20art%20inspired%20geometric%20pattern%2C%20warm%20terracotta%20and%20gold%20tones%2C%20circular%20emblem%20style%2C%20dark%20background%2C%20vibrant%20cultural%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-mexicana&orientation=squarish',
    displayEn: ['radio', 'programa', 'concurso'],
    estado: 'activo',
    inversionTotal: 24000,
    inversionMensual: 4000,
    duracionMeses: 6,
    fechaInicio: '2026-03-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 198000, clicks: 32000, leads: 2800, alcance: 450000, ctr: 16.2, conversionRate: 8.8, periodo: 'Mar-Sep 2026' },
    beneficios: ['Mención en vivo en programa matutino', 'Sorteo mensual de cena para dos', 'Logo destacado en página de concursos'],
    categoriaRadio: 'Regional Mexicana',
    tags: ['taqueria', 'gastronomia', 'cdmx', 'condesa', 'restaurante'],
  },
  {
    id: 3,
    nombreNegocio: 'Librería Café Letras',
    tipo: 'tienda',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20bookstore%20cafe%20brand%20logo%20with%20abstract%20open%20book%20and%20coffee%20cup%20geometric%20design%2C%20warm%20cream%20and%20coffee%20brown%20tones%2C%20modern%20literary%20style%2C%20dark%20background%2C%20cozy%20intellectual%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-03&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Cozy%20independent%20bookstore%20cafe%20interior%20with%20wooden%20bookshelves%20and%20reading%20nooks%2C%20warm%20amber%20lighting%2C%20coffee%20bar%20with%20espresso%20machine%2C%20comfortable%20armchairs%2C%20literary%20atmosphere%2C%20editorial%20interior%20photography%20with%20soft%20warm%20tones%20and%20organic%20composition&width=800&height=450&seq=pat-local-img-03&orientation=landscape',
    ciudad: 'Buenos Aires',
    estado: 'CABA',
    pais: 'Argentina',
    direccion: 'Av. Corrientes 1450, Palermo',
    descripcion: 'Librería independiente con café de especialidad en Palermo. Club de lectura mensual, presentaciones de autores y taller de escritura creativa.',
    sector: 'Cultura y Librerías',
    contactoNombre: 'Valeria Ponce',
    contactoEmail: 'valeria@librerialetras.com.ar',
    contactoTelefono: '+54 11 3456 7890',
    website: 'https://librerialetras.com.ar',
    fechaRegistro: '2026-02-10',
    radioNombre: 'Radio Cultura Digital',
    radioFrecuencia: '91.3 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20cultural%20radio%20station%20logo%20design%20with%20abstract%20book%20and%20sound%20wave%20geometric%20shapes%2C%20elegant%20navy%20and%20gold%20tones%2C%20intellectual%20circular%20emblem%20style%2C%20dark%20background%2C%20sophisticated%20literary%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-cultura&orientation=squarish',
    displayEn: ['radio', 'programa', 'club'],
    estado: 'activo',
    inversionTotal: 15000,
    inversionMensual: 2500,
    duracionMeses: 6,
    fechaInicio: '2026-03-15',
    fechaFin: '2026-09-15',
    metricas: { impresiones: 95000, clicks: 14200, leads: 980, alcance: 280000, ctr: 14.9, conversionRate: 6.9, periodo: 'Mar-Sep 2026' },
    beneficios: ['Mención en programa literario semanal', 'Club de lectura patrocinado', 'Descuento para miembros del club de la radio'],
    categoriaRadio: 'Cultural',
    tags: ['libreria', 'cafe', 'cultura', 'buenos-aires', 'literatura'],
  },
  {
    id: 4,
    nombreNegocio: 'Clínica Dental Sonrisa Perfecta',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20dental%20clinic%20brand%20logo%20with%20abstract%20tooth%20and%20smile%20geometric%20design%2C%20clean%20white%20and%20sky%20blue%20tones%2C%20modern%20healthcare%20professional%20style%2C%20dark%20background%2C%20friendly%20trustworthy%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-04&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20modern%20dental%20clinic%20reception%20area%20with%20clean%20white%20walls%20and%20natural%20light%2C%20comfortable%20seating%2C%20plants%2C%20professional%20healthcare%20environment%2C%20welcoming%20atmosphere%2C%20editorial%20interior%20photography%20with%20soft%20clean%20lighting%20and%20airy%20composition&width=800&height=450&seq=pat-local-img-04&orientation=landscape',
    ciudad: 'Guadalajara',
    estado: 'Jalisco',
    pais: 'México',
    direccion: 'Av. Vallarta 1820, Col. Americana',
    descripcion: 'Clínica dental premium con tecnología de punta. Ortodoncia invisible, blanqueamiento láser y odontología general. 12 años de experiencia.',
    sector: 'Salud',
    contactoNombre: 'Dra. Carolina Mendoza',
    contactoEmail: 'carolina@sonrisaperfecta.mx',
    contactoTelefono: '+52 33 4567 8901',
    website: 'https://sonrisaperfecta.mx',
    fechaRegistro: '2026-01-05',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '98.5 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20star%20and%20signal%20wave%20geometric%20shapes%2C%20energetic%20coral%20and%20gold%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20vibrant%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito&orientation=squarish',
    displayEn: ['radio', 'evento'],
    estado: 'activo',
    inversionTotal: 21000,
    inversionMensual: 3500,
    duracionMeses: 6,
    fechaInicio: '2026-02-01',
    fechaFin: '2026-08-01',
    metricas: { impresiones: 156000, clicks: 21800, leads: 1650, alcance: 380000, ctr: 14.0, conversionRate: 7.6, periodo: 'Feb-Ago 2026' },
    beneficios: ['Mención en programa de salud semanal', 'Jornada gratuita de revisión para oyentes', 'Banner en página de eventos'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['clinica-dental', 'salud', 'guadalajara', 'odontologia', 'servicio'],
  },
  {
    id: 5,
    nombreNegocio: 'Distribuidora El Roble',
    tipo: 'empresa',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20distribution%20company%20brand%20logo%20with%20abstract%20oak%20tree%20and%20delivery%20route%20geometric%20design%2C%20forest%20green%20and%20earthy%20brown%20tones%2C%20modern%20logistics%20corporate%20style%2C%20dark%20background%2C%20reliable%20solid%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-05&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Modern%20distribution%20warehouse%20facility%20with%20organized%20shelving%20and%20forklifts%2C%20clean%20industrial%20environment%2C%20natural%20light%20through%20skylights%2C%20professional%20logistics%20operation%2C%20editorial%20commercial%20photography%20with%20crisp%20lighting%20and%20structured%20composition&width=800&height=450&seq=pat-local-img-05&orientation=landscape',
    ciudad: 'Monterrey',
    estado: 'Nuevo León',
    pais: 'México',
    direccion: 'Parque Industrial Monterrey, Nave 42',
    descripcion: 'Empresa distribuidora de alimentos y bebidas con cobertura en todo el noreste de México. 200 empleados, flota propia de 45 camiones.',
    sector: 'Logística y Distribución',
    contactoNombre: 'Ing. Roberto Garza',
    contactoEmail: 'rgarza@distribuidoraelroble.mx',
    contactoTelefono: '+52 81 5678 9012',
    website: 'https://distribuidoraelroble.mx',
    fechaRegistro: '2025-10-01',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '93.5 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20industrial%20radio%20station%20logo%20with%20abstract%20factory%20gear%20and%20antenna%20geometric%20design%2C%20steel%20blue%20and%20copper%20tones%2C%20bold%20modern%20circular%20emblem%2C%20dark%20background%2C%20strong%20industrial%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria&orientation=squarish',
    displayEn: ['radio', 'programa', 'evento'],
    estado: 'activo',
    inversionTotal: 45000,
    inversionMensual: 7500,
    duracionMeses: 6,
    fechaInicio: '2026-01-01',
    fechaFin: '2026-07-01',
    metricas: { impresiones: 340000, clicks: 48500, leads: 3200, alcance: 820000, ctr: 14.3, conversionRate: 6.6, periodo: 'Ene-Jul 2026' },
    beneficios: ['Menciones en programas de negocios', 'Banner principal en home de la radio', 'Patrocinio de evento corporativo anual'],
    categoriaRadio: 'Noticias y Negocios',
    tags: ['distribucion', 'empresa', 'monterrey', 'logistica', 'corporativo'],
  },
  {
    id: 6,
    nombreNegocio: 'Feria del Barrio San Ángel',
    tipo: 'evento',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20neighborhood%20festival%20brand%20logo%20with%20abstract%20bunting%20flags%20and%20community%20gathering%20geometric%20design%2C%20festive%20coral%20and%20golden%20yellow%20tones%2C%20modern%20celebration%20style%2C%20dark%20background%2C%20joyful%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-06&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20neighborhood%20street%20festival%20with%20colorful%20decorations%20and%20food%20stalls%2C%20families%20enjoying%20outdoor%20activities%2C%20live%20music%20stage%2C%20community%20celebration%20atmosphere%2C%20warm%20afternoon%20light%2C%20editorial%20event%20photography%20with%20festive%20tones%20and%20lively%20composition&width=800&height=450&seq=pat-local-img-06&orientation=landscape',
    ciudad: 'Ciudad de México',
    estado: 'CDMX',
    pais: 'México',
    direccion: 'Plaza San Ángel, San Ángel',
    descripcion: 'Feria comunitaria anual del Barrio San Ángel. Artesanías, gastronomía local, música en vivo y actividades para niños. 15va edición.',
    sector: 'Eventos y Festivales',
    contactoNombre: 'Comité Vecinal San Ángel',
    contactoEmail: 'feria@sanangel.mx',
    contactoTelefono: '+52 55 6789 0123',
    website: 'https://feriasanangel.mx',
    fechaRegistro: '2026-04-01',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '104.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20mexican%20folk%20art%20inspired%20geometric%20pattern%2C%20warm%20terracotta%20and%20gold%20tones%2C%20circular%20emblem%20style%2C%20dark%20background%2C%20vibrant%20cultural%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-mexicana&orientation=squarish',
    displayEn: ['radio', 'evento', 'programa'],
    estado: 'activo',
    inversionTotal: 12000,
    inversionMensual: 4000,
    duracionMeses: 3,
    fechaInicio: '2026-05-01',
    fechaFin: '2026-08-01',
    metricas: { impresiones: 85000, clicks: 15800, leads: 2100, alcance: 240000, ctr: 18.6, conversionRate: 13.3, periodo: 'May-Ago 2026' },
    beneficios: ['Cobertura en vivo del evento', 'Menciones diarias durante un mes previo', 'Sorteo de entradas VIP'],
    categoriaRadio: 'Regional Mexicana',
    tags: ['feria', 'evento', 'cdmx', 'san-angel', 'comunitario'],
  },
  {
    id: 7,
    nombreNegocio: 'Boutique Elegancia',
    tipo: 'tienda',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20fashion%20boutique%20brand%20logo%20with%20abstract%20dress%20form%20and%20diamond%20geometric%20design%2C%20elegant%20rose%20gold%20and%20champagne%20tones%2C%20modern%20luxury%20retail%20style%2C%20dark%20background%2C%20sophisticated%20feminine%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-07&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20women%20fashion%20boutique%20interior%20with%20curated%20clothing%20displays%20and%20warm%20ambient%20lighting%2C%20marble%20floors%2C%20gold%20accents%2C%20exclusive%20retail%20atmosphere%2C%20editorial%20fashion%20photography%20with%20soft%20luxurious%20tones%20and%20sophisticated%20composition&width=800&height=450&seq=pat-local-img-07&orientation=landscape',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    direccion: 'Calle Serrano 85, Salamanca',
    descripcion: 'Boutique de moda femenina en el barrio de Salamanca. Colecciones exclusivas de diseñadores emergentes españoles. Asesoría de imagen personalizada.',
    sector: 'Moda y Retail',
    contactoNombre: 'Elena García-Velasco',
    contactoEmail: 'elena@boutiqueelegancia.es',
    contactoTelefono: '+34 91 123 4567',
    website: 'https://boutiqueelegancia.es',
    fechaRegistro: '2026-03-01',
    radioNombre: 'Estéreo Capital',
    radioFrecuencia: '102.1 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20skyline%20and%20music%20note%20geometric%20design%2C%20sophisticated%20rose%20and%20charcoal%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-capital&orientation=squarish',
    displayEn: ['radio', 'club'],
    estado: 'activo',
    inversionTotal: 28000,
    inversionMensual: 4666,
    duracionMeses: 6,
    fechaInicio: '2026-04-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 175000, clicks: 26800, leads: 1950, alcance: 420000, ctr: 15.3, conversionRate: 7.3, periodo: 'Abr-Oct 2026' },
    beneficios: ['Mención en programa de lifestyle', 'Descuento exclusivo para miembros del club', 'Evento de moda trimestral patrocinado'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['moda', 'boutique', 'madrid', 'retail', 'lujo'],
  },
  {
    id: 8,
    nombreNegocio: 'Taller Mecánico Rápido',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20auto%20repair%20shop%20brand%20logo%20with%20abstract%20wrench%20and%20gear%20geometric%20design%2C%20bold%20red%20and%20charcoal%20gray%20tones%2C%20modern%20automotive%20service%20style%2C%20dark%20background%2C%20trustworthy%20industrial%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-08&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Clean%20professional%20auto%20repair%20garage%20interior%20with%20hydraulic%20lifts%20and%20organized%20tool%20stations%2C%20modern%20equipment%2C%20bright%20LED%20lighting%2C%20mechanic%20working%20on%20vehicle%2C%20editorial%20commercial%20photography%20with%20crisp%20industrial%20lighting%20and%20structured%20composition&width=800&height=450&seq=pat-local-img-08&orientation=landscape',
    ciudad: 'Bogotá',
    estado: 'Cundinamarca',
    pais: 'Colombia',
    direccion: 'Carrera 30 #45-20, Teusaquillo',
    descripcion: 'Taller mecánico multimarca con 20 años de experiencia. Diagnóstico computarizado, mecánica general, eléctrica y aire acondicionado.',
    sector: 'Automotriz',
    contactoNombre: 'Javier Rincón',
    contactoEmail: 'javier@tallermecanicorapido.co',
    contactoTelefono: '+57 601 234 5678',
    website: 'https://tallermecanicorapido.co',
    fechaRegistro: '2026-02-20',
    radioNombre: 'Radio Estéreo Capital',
    radioFrecuencia: '102.1 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20skyline%20and%20music%20note%20geometric%20design%2C%20emerald%20and%20gold%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20vibrant%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-capital-bog&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 9600,
    inversionMensual: 1600,
    duracionMeses: 6,
    fechaInicio: '2026-03-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 72000, clicks: 9800, leads: 820, alcance: 190000, ctr: 13.6, conversionRate: 8.4, periodo: 'Mar-Sep 2026' },
    beneficios: ['Mención en programa automotriz', 'Descuento del 15% para oyentes', 'Revisión gratuita primer diagnóstico'],
    categoriaRadio: 'Rock',
    tags: ['taller', 'mecanico', 'bogota', 'automotriz', 'servicio'],
  },
  {
    id: 9,
    nombreNegocio: 'Pizzería Don Corleone',
    tipo: 'restaurante',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20italian%20pizzeria%20brand%20logo%20with%20abstract%20pizza%20slice%20and%20rolling%20pin%20geometric%20design%2C%20warm%20tomato%20red%20and%20basil%20green%20tones%2C%20modern%20casual%20dining%20style%2C%20dark%20background%2C%20appetizing%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-09&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Authentic%20italian%20pizzeria%20interior%20with%20wood-fired%20brick%20oven%20glowing%20warmly%2C%20rustic%20wooden%20tables%2C%20string%20lights%2C%20fresh%20ingredients%20displayed%2C%20cozy%20neighborhood%20restaurant%20atmosphere%2C%20editorial%20food%20photography%20with%20rich%20warm%20tones%20and%20inviting%20composition&width=800&height=450&seq=pat-local-img-09&orientation=landscape',
    ciudad: 'Buenos Aires',
    estado: 'CABA',
    pais: 'Argentina',
    direccion: 'Av. Santa Fe 3200, Palermo',
    descripcion: 'Pizzería artesanal napolitana en el corazón de Palermo. Horno de leña importado de Nápoles, masa de fermentación lenta 48hs, ingredientes italianos DOP.',
    sector: 'Gastronomía',
    contactoNombre: 'Marco Rossi',
    contactoEmail: 'marco@doncorleone.com.ar',
    contactoTelefono: '+54 11 4567 8901',
    website: 'https://doncorleone.com.ar',
    fechaRegistro: '2026-01-10',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '103.2 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20flame%20and%20rhythm%20wave%20geometric%20shapes%2C%20warm%20coral%20and%20midnight%20blue%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20passionate%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito-ba&orientation=squarish',
    displayEn: ['radio', 'programa', 'concurso', 'evento'],
    estado: 'activo',
    inversionTotal: 32000,
    inversionMensual: 5333,
    duracionMeses: 6,
    fechaInicio: '2026-02-01',
    fechaFin: '2026-08-01',
    metricas: { impresiones: 245000, clicks: 42000, leads: 3800, alcance: 580000, ctr: 17.1, conversionRate: 9.0, periodo: 'Feb-Ago 2026' },
    beneficios: ['Mención en programa gastronómico semanal', 'Concurso mensual Cena para Dos', 'Patrocinador del evento Noche de Pizzas', 'Logo en home de la radio'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['pizzeria', 'gastronomia', 'buenos-aires', 'italiano', 'restaurante'],
  },
  {
    id: 10,
    nombreNegocio: 'Jardín Infantil Pequeños Genios',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20preschool%20kindergarten%20brand%20logo%20with%20abstract%20building%20blocks%20and%20star%20geometric%20design%2C%20playful%20pastel%20rainbow%20tones%2C%20modern%20early%20education%20style%2C%20dark%20background%2C%20cheerful%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-10&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20cheerful%20preschool%20classroom%20interior%20with%20colorful%20learning%20materials%20and%20play%20areas%2C%20natural%20light%20through%20large%20windows%2C%20child-friendly%20furniture%2C%20educational%20toys%2C%20warm%20welcoming%20atmosphere%2C%20editorial%20interior%20photography%20with%20soft%20natural%20lighting%20and%20joyful%20composition&width=800&height=450&seq=pat-local-img-10&orientation=landscape',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    direccion: 'Barrio Escalante, Calle 33',
    descripcion: 'Centro educativo infantil bilingüe con metodología Montessori. Grupos reducidos, huerto orgánico, música y arte. De 1 a 6 años.',
    sector: 'Educación',
    contactoNombre: 'Lic. María Fernanda Solís',
    contactoEmail: 'maria@pequenosgenios.cr',
    contactoTelefono: '+506 2222 4444',
    website: 'https://pequenosgenios.cr',
    fechaRegistro: '2026-03-15',
    radioNombre: 'Radio Costa Azul',
    radioFrecuencia: '88.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20ocean%20wave%20and%20palm%20tree%20geometric%20design%2C%20soothing%20aqua%20and%20sand%20tones%2C%20tropical%20modern%20circular%20emblem%2C%20dark%20background%2C%20beach%20lifestyle%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-costa&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 14400,
    inversionMensual: 2400,
    duracionMeses: 6,
    fechaInicio: '2026-04-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 88000, clicks: 12800, leads: 1100, alcance: 220000, ctr: 14.5, conversionRate: 8.6, periodo: 'Abr-Oct 2026' },
    beneficios: ['Mención en programa familiar matutino', 'Jornada de puertas abiertas para oyentes', 'Material educativo de regalo'],
    categoriaRadio: 'Pop Latino',
    tags: ['jardin-infantil', 'educacion', 'san-jose', 'montessori', 'servicio'],
  },
  {
    id: 11,
    nombreNegocio: 'Supermercado La Económica',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20supermarket%20grocery%20store%20brand%20logo%20with%20abstract%20shopping%20cart%20and%20leaf%20geometric%20design%2C%20fresh%20green%20and%20warm%20yellow%20tones%2C%20modern%20local%20market%20style%2C%20dark%20background%2C%20friendly%20community%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-11&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20modern%20neighborhood%20grocery%20store%20interior%20with%20fresh%20produce%20displays%20and%20organized%20aisles%2C%20warm%20ambient%20lighting%2C%20local%20products%20section%2C%20community%20bulletin%20board%2C%20editorial%20commercial%20photography%20with%20clean%20inviting%20tones%20and%20organic%20composition&width=800&height=450&seq=pat-local-img-11&orientation=landscape',
    ciudad: 'Guadalajara',
    estado: 'Jalisco',
    pais: 'México',
    direccion: 'Av. Chapultepec 890, Col. Americana',
    descripcion: 'Supermercado de barrio con productos frescos, locales y a precios justos. Carnicería, frutería y abarrotes. 3 sucursales en la ciudad.',
    sector: 'Comercio y Alimentación',
    contactoNombre: 'Familia Mendoza López',
    contactoEmail: 'info@supereconomica.mx',
    contactoTelefono: '+52 33 5678 9012',
    website: 'https://supereconomica.mx',
    fechaRegistro: '2025-12-01',
    radioNombre: 'Radio Deportiva Total',
    radioFrecuencia: '95.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20sports%20radio%20station%20logo%20with%20abstract%20trophy%20and%20ball%20geometric%20design%2C%20energetic%20red%20and%20black%20tones%2C%20bold%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20competitive%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-deportiva&orientation=squarish',
    displayEn: ['radio', 'concurso'],
    estado: 'activo',
    inversionTotal: 20000,
    inversionMensual: 3333,
    duracionMeses: 6,
    fechaInicio: '2026-01-01',
    fechaFin: '2026-07-01',
    metricas: { impresiones: 165000, clicks: 24000, leads: 1800, alcance: 400000, ctr: 14.5, conversionRate: 7.5, periodo: 'Ene-Jul 2026' },
    beneficios: ['Mención en programa deportivo', 'Sorteo semanal de despensa', 'Cupones de descuento en tienda'],
    categoriaRadio: 'Deportiva',
    tags: ['supermercado', 'abarrotes', 'guadalajara', 'comercio', 'local'],
  },
  {
    id: 12,
    nombreNegocio: 'Constructora Casas del Sol',
    tipo: 'empresa',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20construction%20company%20brand%20logo%20with%20abstract%20house%20and%20sunrise%20geometric%20design%2C%20warm%20terracotta%20and%20golden%20yellow%20tones%2C%20modern%20real%20estate%20style%2C%20dark%20background%2C%20solid%20trustworthy%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-12&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Modern%20residential%20construction%20site%20with%20partially%20built%20homes%20showing%20quality%20craftsmanship%2C%20clean%20organized%20worksite%2C%20natural%20afternoon%20light%2C%20professional%20building%20operation%2C%20editorial%20architectural%20photography%20with%20crisp%20lighting%20and%20structured%20composition&width=800&height=450&seq=pat-local-img-12&orientation=landscape',
    ciudad: 'Bogotá',
    estado: 'Cundinamarca',
    pais: 'Colombia',
    direccion: 'Calle 100 #15-30, Usaquén',
    descripcion: 'Constructora especializada en vivienda de interés social y medio. 500+ viviendas entregadas. Proyectos en Bogotá, Chía y Cajicá.',
    sector: 'Construcción',
    contactoNombre: 'Arq. Andrés Castillo',
    contactoEmail: 'acastillo@casasdelsol.co',
    contactoTelefono: '+57 601 345 6789',
    website: 'https://casasdelsol.co',
    fechaRegistro: '2025-11-01',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '96.0 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20building%20and%20antenna%20geometric%20design%2C%20warm%20amber%20and%20steel%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria-bog&orientation=squarish',
    displayEn: ['radio', 'programa', 'evento', 'club'],
    estado: 'activo',
    inversionTotal: 52000,
    inversionMensual: 8666,
    duracionMeses: 6,
    fechaInicio: '2026-01-15',
    fechaFin: '2026-07-15',
    metricas: { impresiones: 410000, clicks: 58000, leads: 4200, alcance: 950000, ctr: 14.1, conversionRate: 7.2, periodo: 'Ene-Jul 2026' },
    beneficios: ['Programa semanal de vivienda y construcción', 'Patrocinio de eventos de la radio', 'Club de beneficios para oyentes', 'Banner principal en web de la radio'],
    categoriaRadio: 'Noticias',
    tags: ['construccion', 'empresa', 'bogota', 'vivienda', 'corporativo'],
  },
  {
    id: 13,
    nombreNegocio: 'Festival de Música Independiente',
    tipo: 'evento',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20indie%20music%20festival%20brand%20logo%20with%20abstract%20guitar%20pick%20and%20sound%20wave%20geometric%20design%2C%20vibrant%20magenta%20and%20electric%20blue%20tones%2C%20modern%20music%20event%20style%2C%20dark%20background%2C%20artistic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-13&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Outdoor%20indie%20music%20festival%20with%20main%20stage%20and%20crowd%20enjoying%20live%20performance%2C%20colorful%20lighting%20rig%2C%20summer%20evening%20atmosphere%2C%20artistic%20festival%20vibes%2C%20editorial%20event%20photography%20with%20dramatic%20stage%20lighting%20and%20energetic%20composition&width=800&height=450&seq=pat-local-img-13&orientation=landscape',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    direccion: 'Parque Juan Carlos I, Madrid',
    descripcion: 'Festival anual de música independiente con artistas emergentes de toda España. 3 escenarios, food trucks, mercado de vinilos. 8va edición.',
    sector: 'Música y Entretenimiento',
    contactoNombre: 'Diego Montoya',
    contactoEmail: 'diego@festivalindie.es',
    contactoTelefono: '+34 91 234 5678',
    website: 'https://festivalindie.es',
    fechaRegistro: '2026-05-01',
    radioNombre: 'Estéreo Capital',
    radioFrecuencia: '102.1 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20skyline%20and%20music%20note%20geometric%20design%2C%20sophisticated%20rose%20and%20charcoal%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-capital&orientation=squarish',
    displayEn: ['radio', 'evento', 'programa'],
    estado: 'activo',
    inversionTotal: 18000,
    inversionMensual: 6000,
    duracionMeses: 3,
    fechaInicio: '2026-06-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 135000, clicks: 25000, leads: 3200, alcance: 360000, ctr: 18.5, conversionRate: 12.8, periodo: 'Jun-Sep 2026' },
    beneficios: ['Cobertura exclusiva del festival', 'Entrevistas con artistas en vivo', 'Sorteo de abonos dobles entre oyentes'],
    categoriaRadio: 'Rock y Alternativo',
    tags: ['festival', 'musica', 'madrid', 'indie', 'evento'],
  },
  {
    id: 14,
    nombreNegocio: 'Florería Pétalos',
    tipo: 'tienda',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20flower%20shop%20brand%20logo%20with%20abstract%20rose%20and%20leaf%20geometric%20design%2C%20soft%20pink%20and%20sage%20green%20tones%2C%20modern%20botanical%20style%2C%20dark%20background%2C%20elegant%20natural%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-14&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Beautiful%20flower%20shop%20interior%20with%20fresh%20bouquets%20and%20arrangements%20displayed%2C%20natural%20light%20through%20storefront%2C%20rustic%20wooden%20display%20tables%2C%20colorful%20blooms%2C%20romantic%20atmosphere%2C%20editorial%20photography%20with%20soft%20natural%20lighting%20and%20floral%20composition&width=800&height=450&seq=pat-local-img-14&orientation=landscape',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    direccion: 'Avenida Central, Calle 5',
    descripcion: 'Florería boutique con diseños personalizados para toda ocasión. Flores importadas de Ecuador y Holanda. Envíos a domicilio en el GAM.',
    sector: 'Flores y Decoración',
    contactoNombre: 'Ana Lucía Vargas',
    contactoEmail: 'ana@floreriapetalos.cr',
    contactoTelefono: '+506 2222 5555',
    website: 'https://floreriapetalos.cr',
    fechaRegistro: '2026-04-15',
    radioNombre: 'Radio Costa Azul',
    radioFrecuencia: '88.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20ocean%20wave%20and%20palm%20tree%20geometric%20design%2C%20soothing%20aqua%20and%20sand%20tones%2C%20tropical%20modern%20circular%20emblem%2C%20dark%20background%2C%20beach%20lifestyle%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-costa&orientation=squarish',
    displayEn: ['radio', 'programa', 'concurso'],
    estado: 'activo',
    inversionTotal: 8400,
    inversionMensual: 1400,
    duracionMeses: 6,
    fechaInicio: '2026-05-01',
    fechaFin: '2026-11-01',
    metricas: { impresiones: 62000, clicks: 8500, leads: 720, alcance: 160000, ctr: 13.7, conversionRate: 8.5, periodo: 'May-Nov 2026' },
    beneficios: ['Mención en programa de la tarde', 'Concurso mensual de ramo sorpresa', 'Descuento para oyentes'],
    categoriaRadio: 'Balada y Romántica',
    tags: ['floreria', 'decoracion', 'san-jose', 'flores', 'tienda'],
  },
  {
    id: 15,
    nombreNegocio: 'Gimnasio IronBody',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20fitness%20gym%20brand%20logo%20with%20abstract%20dumbbell%20and%20lightning%20bolt%20geometric%20design%2C%20energetic%20orange%20and%20charcoal%20tones%2C%20modern%20athletic%20style%2C%20dark%20background%2C%20powerful%20dynamic%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-15&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Modern%20fitness%20gym%20interior%20with%20state-of-the-art%20equipment%20and%20weight%20training%20area%2C%20dramatic%20lighting%2C%20industrial%20aesthetic%2C%20people%20working%20out%2C%20mirrors%2C%20editorial%20fitness%20photography%20with%20energetic%20lighting%20and%20dynamic%20composition&width=800&height=450&seq=pat-local-img-15&orientation=landscape',
    ciudad: 'Monterrey',
    estado: 'Nuevo León',
    pais: 'México',
    direccion: 'Av. Gómez Morín 950, San Pedro',
    descripcion: 'Gimnasio premium con equipamiento de última generación. Clases grupales, entrenadores personales certificados, zona de crossfit y área de recuperación.',
    sector: 'Salud y Fitness',
    contactoNombre: 'Coach Daniel Treviño',
    contactoEmail: 'daniel@ironbody.mx',
    contactoTelefono: '+52 81 6789 0123',
    website: 'https://ironbody.mx',
    fechaRegistro: '2026-03-01',
    radioNombre: 'Radio Deportiva Total',
    radioFrecuencia: '95.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20sports%20radio%20station%20logo%20with%20abstract%20trophy%20and%20ball%20geometric%20design%2C%20energetic%20red%20and%20black%20tones%2C%20bold%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20competitive%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-deportiva&orientation=squarish',
    displayEn: ['radio', 'programa', 'club', 'concurso'],
    estado: 'activo',
    inversionTotal: 36000,
    inversionMensual: 6000,
    duracionMeses: 6,
    fechaInicio: '2026-04-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 280000, clicks: 45000, leads: 3800, alcance: 650000, ctr: 16.1, conversionRate: 8.4, periodo: 'Abr-Oct 2026' },
    beneficios: ['Programa fitness semanal patrocinado', 'Reto IronBody para el club de fans', 'Membresía de prueba gratuita para oyentes', 'Sorteo de 3 membresías anuales'],
    categoriaRadio: 'Deportiva',
    tags: ['gimnasio', 'fitness', 'monterrey', 'salud', 'servicio'],
  },
  {
    id: 16,
    nombreNegocio: 'Café La Taza Roja',
    tipo: 'restaurante',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20coffee%20shop%20brand%20logo%20with%20abstract%20coffee%20cup%20and%20steam%20geometric%20design%2C%20warm%20burgundy%20red%20and%20cream%20tones%2C%20modern%20artisanal%20cafe%20style%2C%20dark%20background%2C%20cozy%20inviting%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-16&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Cozy%20artisan%20coffee%20shop%20interior%20with%20exposed%20brick%20walls%20and%20warm%20Edison%20bulb%20lighting%2C%20barista%20preparing%20pour-over%20coffee%2C%20rustic%20wooden%20furniture%2C%20relaxed%20neighborhood%20cafe%20atmosphere%2C%20editorial%20photography%20with%20warm%20amber%20tones%20and%20inviting%20composition&width=800&height=450&seq=pat-local-img-16&orientation=landscape',
    ciudad: 'Buenos Aires',
    estado: 'CABA',
    pais: 'Argentina',
    direccion: 'Calle Thames 1750, Palermo Soho',
    descripcion: 'Café de especialidad con granos de origen único tostados in-house. Métodos de extracción alternativos, pastelería artesanal y brunch los fines de semana.',
    sector: 'Gastronomía',
    contactoNombre: 'Sofía Martínez',
    contactoEmail: 'sofia@latazaroja.com.ar',
    contactoTelefono: '+54 11 5678 9012',
    website: 'https://latazaroja.com.ar',
    fechaRegistro: '2026-02-01',
    radioNombre: 'Radio Cultura Digital',
    radioFrecuencia: '91.3 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20cultural%20radio%20station%20logo%20design%20with%20abstract%20book%20and%20sound%20wave%20geometric%20shapes%2C%20elegant%20navy%20and%20gold%20tones%2C%20intellectual%20circular%20emblem%20style%2C%20dark%20background%2C%20sophisticated%20literary%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-cultura&orientation=squarish',
    displayEn: ['radio', 'club'],
    estado: 'activo',
    inversionTotal: 12600,
    inversionMensual: 2100,
    duracionMeses: 6,
    fechaInicio: '2026-03-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 78000, clicks: 11500, leads: 920, alcance: 200000, ctr: 14.7, conversionRate: 8.0, periodo: 'Mar-Sep 2026' },
    beneficios: ['Mención en programa cultural matutino', 'Café de bienvenida para nuevos miembros del club', 'Tertulia literaria mensual patrocinada'],
    categoriaRadio: 'Cultural',
    tags: ['cafeteria', 'cafe', 'buenos-aires', 'palermo', 'restaurante'],
  },
  {
    id: 17,
    nombreNegocio: 'Agencia de Viajes MundoExplora',
    tipo: 'empresa',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20travel%20agency%20brand%20logo%20with%20abstract%20globe%20and%20airplane%20geometric%20design%2C%20sky%20blue%20and%20sunset%20orange%20tones%2C%20modern%20travel%20style%2C%20dark%20background%2C%20adventurous%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-17&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20modern%20travel%20agency%20office%20with%20world%20maps%20on%20walls%20and%20destination%20posters%2C%20friendly%20travel%20consultants%20at%20desks%2C%20natural%20light%2C%20inspiring%20travel%20atmosphere%2C%20editorial%20commercial%20photography%20with%20warm%20inviting%20tones%20and%20professional%20composition&width=800&height=450&seq=pat-local-img-17&orientation=landscape',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    direccion: 'Gran Vía 45, Centro',
    descripcion: 'Agencia de viajes especializada en destinos exóticos y viajes a medida. 15 años creando experiencias inolvidables en los 5 continentes.',
    sector: 'Turismo y Viajes',
    contactoNombre: 'Carlos Almeida',
    contactoEmail: 'carlos@mundoexplora.es',
    contactoTelefono: '+34 91 345 6789',
    website: 'https://mundoexplora.es',
    fechaRegistro: '2025-09-15',
    radioNombre: 'Estéreo Capital',
    radioFrecuencia: '102.1 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20skyline%20and%20music%20note%20geometric%20design%2C%20sophisticated%20rose%20and%20charcoal%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-capital&orientation=squarish',
    displayEn: ['radio', 'programa', 'concurso'],
    estado: 'activo',
    inversionTotal: 38000,
    inversionMensual: 6333,
    duracionMeses: 6,
    fechaInicio: '2026-01-01',
    fechaFin: '2026-07-01',
    metricas: { impresiones: 290000, clicks: 42000, leads: 3500, alcance: 700000, ctr: 14.5, conversionRate: 8.3, periodo: 'Ene-Jul 2026' },
    beneficios: ['Programa semanal de viajes y destinos', 'Sorteo trimestral de viaje para dos', 'Descuentos exclusivos para oyentes'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['viajes', 'turismo', 'madrid', 'agencia', 'empresa'],
  },
  {
    id: 18,
    nombreNegocio: 'Panadería El Trigo Dorado',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20artisanal%20bakery%20brand%20logo%20with%20abstract%20wheat%20stalk%20and%20bread%20loaf%20geometric%20design%2C%20warm%20golden%20brown%20and%20cream%20tones%2C%20modern%20traditional%20baking%20style%2C%20dark%20background%2C%20homey%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-18&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Traditional%20neighborhood%20bakery%20interior%20with%20fresh%20bread%20and%20pastries%20displayed%20in%20glass%20counter%2C%20warm%20oven%20glow%2C%20wooden%20shelves%2C%20rustic%20charm%2C%20morning%20light%20through%20windows%2C%20editorial%20food%20photography%20with%20warm%20amber%20tones%20and%20inviting%20composition&width=800&height=450&seq=pat-local-img-18&orientation=landscape',
    ciudad: 'Ciudad de México',
    estado: 'CDMX',
    pais: 'México',
    direccion: 'Calle Oaxaca 89, Col. Roma Sur',
    descripcion: 'Panadería artesanal de barrio desde 1965. Pan de masa madre, conchas, bolillo recién horneado y repostería tradicional mexicana.',
    sector: 'Alimentación',
    contactoNombre: 'Don Ramón Vega',
    contactoEmail: 'ramon@eltrigodorado.mx',
    contactoTelefono: '+52 55 7890 1234',
    website: 'https://eltrigodorado.mx',
    fechaRegistro: '2026-01-20',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '104.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20mexican%20folk%20art%20inspired%20geometric%20pattern%2C%20warm%20terracotta%20and%20gold%20tones%2C%20circular%20emblem%20style%2C%20dark%20background%2C%20vibrant%20cultural%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-mexicana&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 9000,
    inversionMensual: 1500,
    duracionMeses: 6,
    fechaInicio: '2026-02-01',
    fechaFin: '2026-08-01',
    metricas: { impresiones: 68000, clicks: 9200, leads: 780, alcance: 180000, ctr: 13.5, conversionRate: 8.5, periodo: 'Feb-Ago 2026' },
    beneficios: ['Mención mañanera diaria', 'Pan de cortesía para la cabina', 'Cupón digital en web de la radio'],
    categoriaRadio: 'Regional Mexicana',
    tags: ['panaderia', 'tradicional', 'cdmx', 'roma', 'local'],
  },
  {
    id: 19,
    nombreNegocio: 'Estudio Jurídico LexNova',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20law%20firm%20brand%20logo%20with%20abstract%20scales%20of%20justice%20and%20column%20geometric%20design%2C%20navy%20blue%20and%20gold%20tones%2C%20modern%20legal%20professional%20style%2C%20dark%20background%2C%20authoritative%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-19&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20modern%20law%20firm%20office%20interior%20with%20mahogany%20bookshelves%20and%20leather%20furniture%2C%20professional%20atmosphere%2C%20natural%20light%20through%20tall%20windows%2C%20sophisticated%20legal%20practice%20environment%2C%20editorial%20interior%20photography%20with%20refined%20tones%20and%20composed%20setting&width=800&height=450&seq=pat-local-img-19&orientation=landscape',
    ciudad: 'Bogotá',
    estado: 'Cundinamarca',
    pais: 'Colombia',
    direccion: 'Carrera 7 #72-40, Oficina 1501, Chapinero',
    descripcion: 'Firma de abogados especializada en derecho comercial, propiedad intelectual y resolución de conflictos. Equipo de 12 profesionales.',
    sector: 'Legal',
    contactoNombre: 'Dr. Felipe Rojas',
    contactoEmail: 'frojas@lexnova.co',
    contactoTelefono: '+57 601 456 7890',
    website: 'https://lexnova.co',
    fechaRegistro: '2026-03-01',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '96.0 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20building%20and%20antenna%20geometric%20design%2C%20warm%20amber%20and%20steel%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria-bog&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 27000,
    inversionMensual: 4500,
    duracionMeses: 6,
    fechaInicio: '2026-04-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 195000, clicks: 28000, leads: 2400, alcance: 460000, ctr: 14.4, conversionRate: 8.6, periodo: 'Abr-Oct 2026' },
    beneficios: ['Segmento legal semanal en programa de noticias', 'Consultas gratuitas para oyentes', 'Guías legales descargables'],
    categoriaRadio: 'Noticias',
    tags: ['abogados', 'legal', 'bogota', 'servicio', 'profesional'],
  },
  {
    id: 20,
    nombreNegocio: 'Carnicería Don Jorge',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20butcher%20shop%20brand%20logo%20with%20abstract%20cleaver%20and%20meat%20cut%20geometric%20design%2C%20rich%20burgundy%20and%20cream%20tones%2C%20modern%20traditional%20butcher%20style%2C%20dark%20background%2C%20artisanal%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-20&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Traditional%20neighborhood%20butcher%20shop%20with%20fresh%20meat%20cuts%20displayed%20in%20glass%20case%2C%20wooden%20cutting%20blocks%2C%20clean%20professional%20environment%2C%20warm%20lighting%2C%20artisan%20quality%2C%20editorial%20commercial%20photography%20with%20rich%20warm%20tones%20and%20authentic%20composition&width=800&height=450&seq=pat-local-img-20&orientation=landscape',
    ciudad: 'Buenos Aires',
    estado: 'CABA',
    pais: 'Argentina',
    direccion: 'Av. Rivadavia 5400, Caballito',
    descripcion: 'Carnicería premium con cortes argentinos de primera calidad. Angus certificado, cortes especiales para asado, atención personalizada. 40 años en el barrio.',
    sector: 'Alimentación',
    contactoNombre: 'Jorge Fernández',
    contactoEmail: 'jorge@carniceriadonjorge.com.ar',
    contactoTelefono: '+54 11 6789 0123',
    website: 'https://carniceriadonjorge.com.ar',
    fechaRegistro: '2025-11-01',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '103.2 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20flame%20and%20rhythm%20wave%20geometric%20shapes%2C%20warm%20coral%20and%20midnight%20blue%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20passionate%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito-ba&orientation=squarish',
    displayEn: ['radio', 'concurso'],
    estado: 'activo',
    inversionTotal: 15000,
    inversionMensual: 2500,
    duracionMeses: 6,
    fechaInicio: '2026-01-01',
    fechaFin: '2026-07-01',
    metricas: { impresiones: 110000, clicks: 15800, leads: 1200, alcance: 280000, ctr: 14.4, conversionRate: 7.6, periodo: 'Ene-Jul 2026' },
    beneficios: ['Mención en programa gastronómico', 'Sorteo mensual de asado completo', 'Recetas exclusivas al aire'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['carniceria', 'asado', 'buenos-aires', 'alimentacion', 'local'],
  },
  {
    id: 21,
    nombreNegocio: 'Farmacia Salud y Bienestar',
    tipo: 'tienda',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20pharmacy%20drugstore%20brand%20logo%20with%20abstract%20cross%20and%20leaf%20geometric%20design%2C%20fresh%20green%20and%20white%20tones%2C%20modern%20healthcare%20retail%20style%2C%20dark%20background%2C%20trustworthy%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-21&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20modern%20pharmacy%20interior%20with%20organized%20shelves%20of%20healthcare%20products%2C%20professional%20service%20counter%2C%20clean%20clinical%20environment%20with%20warm%20touches%2C%20natural%20light%2C%20community%20health%20atmosphere%2C%20editorial%20commercial%20photography%20with%20clean%20bright%20tones&width=800&height=450&seq=pat-local-img-21&orientation=landscape',
    ciudad: 'Guadalajara',
    estado: 'Jalisco',
    pais: 'México',
    direccion: 'Av. López Mateos 2500, Col. Providencia',
    descripcion: 'Farmacia con servicio de 24 horas, medicamentos genéricos y de patente, línea de cuidado personal y servicio de entrega a domicilio.',
    sector: 'Salud y Farmacia',
    contactoNombre: 'QF. Laura Medina',
    contactoEmail: 'laura@farmaciasalud.mx',
    contactoTelefono: '+52 33 7890 1234',
    website: 'https://farmaciasalud.mx',
    fechaRegistro: '2026-02-15',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '98.5 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20star%20and%20signal%20wave%20geometric%20shapes%2C%20energetic%20coral%20and%20gold%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20vibrant%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 18000,
    inversionMensual: 3000,
    duracionMeses: 6,
    fechaInicio: '2026-03-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 142000, clicks: 19500, leads: 1450, alcance: 340000, ctr: 13.7, conversionRate: 7.4, periodo: 'Mar-Sep 2026' },
    beneficios: ['Cápsula de salud semanal', 'Descuentos en medicamentos genéricos', 'Directorio de servicios en web'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['farmacia', 'salud', 'guadalajara', 'medicamentos', 'tienda'],
  },
  {
    id: 22,
    nombreNegocio: 'CineClub La Linterna',
    tipo: 'evento',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20film%20club%20brand%20logo%20with%20abstract%20projector%20and%20film%20reel%20geometric%20design%2C%20warm%20amber%20and%20charcoal%20tones%2C%20modern%20cinema%20style%2C%20dark%20background%2C%20artistic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-22&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Intimate%20independent%20cinema%20screening%20room%20with%20red%20velvet%20seats%20and%20warm%20ambient%20lighting%2C%20projected%20film%20on%20screen%2C%20cultural%20atmosphere%2C%20small%20audience%20enjoying%20art%20film%2C%20editorial%20photography%20with%20cinematic%20tones%20and%20cozy%20composition&width=800&height=450&seq=pat-local-img-22&orientation=landscape',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    direccion: 'Barrio Amón, Calle 7',
    descripcion: 'Cineclub independiente con proyecciones semanales de cine de autor, documentales y clásicos restaurados. Foro de discusión post-función.',
    sector: 'Cultura y Cine',
    contactoNombre: 'Mauricio Solano',
    contactoEmail: 'mauricio@cinelalinterna.cr',
    contactoTelefono: '+506 2222 6666',
    website: 'https://cinelalinterna.cr',
    fechaRegistro: '2026-05-15',
    radioNombre: 'Radio Costa Azul',
    radioFrecuencia: '88.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20ocean%20wave%20and%20palm%20tree%20geometric%20design%2C%20soothing%20aqua%20and%20sand%20tones%2C%20tropical%20modern%20circular%20emblem%2C%20dark%20background%2C%20beach%20lifestyle%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-costa&orientation=squarish',
    displayEn: ['radio', 'evento', 'club'],
    estado: 'activo',
    inversionTotal: 7200,
    inversionMensual: 2400,
    duracionMeses: 3,
    fechaInicio: '2026-06-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 45000, clicks: 8200, leads: 650, alcance: 120000, ctr: 18.2, conversionRate: 7.9, periodo: 'Jun-Sep 2026' },
    beneficios: ['Cartelera semanal en programa cultural', 'Funciones gratuitas para el club de fans', 'Entrevistas con directores'],
    categoriaRadio: 'Cultural',
    tags: ['cine', 'cultura', 'san-jose', 'cineclub', 'evento'],
  },
  {
    id: 23,
    nombreNegocio: 'TecnoRepair Express',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20electronics%20repair%20shop%20brand%20logo%20with%20abstract%20screwdriver%20and%20circuit%20board%20geometric%20design%2C%20electric%20blue%20and%20silver%20tones%2C%20modern%20tech%20repair%20style%2C%20dark%20background%2C%20precise%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-23&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Clean%20organized%20electronics%20repair%20workshop%20with%20technician%20repairing%20smartphone%20at%20workbench%2C%20precision%20tools%2C%20organized%20component%20storage%2C%20modern%20equipment%2C%20professional%20atmosphere%2C%20editorial%20commercial%20photography%20with%20crisp%20lighting%20and%20technical%20composition&width=800&height=450&seq=pat-local-img-23&orientation=landscape',
    ciudad: 'Monterrey',
    estado: 'Nuevo León',
    pais: 'México',
    direccion: 'Av. Universidad 1200, Centro',
    descripcion: 'Servicio técnico especializado en reparación de celulares, tablets y laptops. Diagnóstico gratuito, garantía de 90 días, servicio express.',
    sector: 'Tecnología',
    contactoNombre: 'Ing. Oscar Núñez',
    contactoEmail: 'oscar@tecnorepair.mx',
    contactoTelefono: '+52 81 8901 2345',
    website: 'https://tecnorepair.mx',
    fechaRegistro: '2026-04-01',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '93.5 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20industrial%20radio%20station%20logo%20with%20abstract%20factory%20gear%20and%20antenna%20geometric%20design%2C%20steel%20blue%20and%20copper%20tones%2C%20bold%20modern%20circular%20emblem%2C%20dark%20background%2C%20strong%20industrial%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 13200,
    inversionMensual: 2200,
    duracionMeses: 6,
    fechaInicio: '2026-05-01',
    fechaFin: '2026-11-01',
    metricas: { impresiones: 98000, clicks: 14800, leads: 1100, alcance: 250000, ctr: 15.1, conversionRate: 7.4, periodo: 'May-Nov 2026' },
    beneficios: ['Segmento tecnológico semanal', 'Diagnóstico gratuito para oyentes', 'Tips de tecnología al aire'],
    categoriaRadio: 'Noticias y Negocios',
    tags: ['reparacion', 'tecnologia', 'monterrey', 'celulares', 'servicio'],
  },
  {
    id: 24,
    nombreNegocio: 'Mercería Hilos y Botones',
    tipo: 'tienda',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20haberdashery%20sewing%20shop%20brand%20logo%20with%20abstract%20thread%20spool%20and%20needle%20geometric%20design%2C%20soft%20lavender%20and%20cream%20tones%2C%20modern%20craft%20style%2C%20dark%20background%2C%20delicate%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-24&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Charming%20traditional%20haberdashery%20shop%20interior%20with%20colorful%20thread%20spools%20and%20fabric%20rolls%20displayed%2C%20vintage%20wooden%20cabinets%2C%20sewing%20notions%2C%20craft%20atmosphere%2C%20warm%20lighting%2C%20editorial%20photography%20with%20soft%20tones%20and%20artisanal%20composition&width=800&height=450&seq=pat-local-img-24&orientation=landscape',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    direccion: 'Calle Fuencarral 78, Malasaña',
    descripcion: 'Mercería tradicional con más de 50 años en Malasaña. Hilos, botones, telas, lanas y accesorios de costura. Talleres de bordado y crochet.',
    sector: 'Manualidades y Textil',
    contactoNombre: 'Doña Carmen Ruiz',
    contactoEmail: 'carmen@hilosybotones.es',
    contactoTelefono: '+34 91 456 7890',
    website: 'https://hilosybotones.es',
    fechaRegistro: '2026-03-20',
    radioNombre: 'Estéreo Capital',
    radioFrecuencia: '102.1 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20skyline%20and%20music%20note%20geometric%20design%2C%20sophisticated%20rose%20and%20charcoal%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-capital&orientation=squarish',
    displayEn: ['radio', 'club'],
    estado: 'activo',
    inversionTotal: 7200,
    inversionMensual: 1200,
    duracionMeses: 6,
    fechaInicio: '2026-04-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 52000, clicks: 7200, leads: 580, alcance: 140000, ctr: 13.8, conversionRate: 8.1, periodo: 'Abr-Oct 2026' },
    beneficios: ['Mención en programa de tarde', 'Taller de bordado para el club', 'Kit de bienvenida para nuevos oyentes'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['merceria', 'manualidades', 'madrid', 'costura', 'tienda'],
  },
  {
    id: 25,
    nombreNegocio: 'Restaurante Sabor Marino',
    tipo: 'restaurante',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20seafood%20restaurant%20brand%20logo%20with%20abstract%20fish%20and%20wave%20geometric%20design%2C%20ocean%20blue%20and%20coral%20tones%2C%20modern%20coastal%20dining%20style%2C%20dark%20background%2C%20fresh%20elegant%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-25&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Elegant%20seafood%20restaurant%20interior%20with%20nautical%20decor%20and%20fresh%20seafood%20display%20on%20ice%2C%20ocean%20view%20windows%2C%20warm%20ambient%20lighting%2C%20upscale%20coastal%20dining%20atmosphere%2C%20editorial%20food%20photography%20with%20fresh%20oceanic%20tones%20and%20sophisticated%20composition&width=800&height=450&seq=pat-local-img-25&orientation=landscape',
    ciudad: 'San José',
    estado: 'San José',
    pais: 'Costa Rica',
    direccion: 'Paseo Colón, Calle 40',
    descripcion: 'Restaurante de mariscos con los productos más frescos del Pacífico y Caribe costarricense. Ceviches, pescados enteros y mariscadas.',
    sector: 'Gastronomía',
    contactoNombre: 'Chef Ricardo Molina',
    contactoEmail: 'ricardo@sabormarino.cr',
    contactoTelefono: '+506 2222 7777',
    website: 'https://sabormarino.cr',
    fechaRegistro: '2026-04-01',
    radioNombre: 'Radio Costa Azul',
    radioFrecuencia: '88.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20ocean%20wave%20and%20palm%20tree%20geometric%20design%2C%20soothing%20aqua%20and%20sand%20tones%2C%20tropical%20modern%20circular%20emblem%2C%20dark%20background%2C%20beach%20lifestyle%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-costa&orientation=squarish',
    displayEn: ['radio', 'programa', 'evento'],
    estado: 'activo',
    inversionTotal: 16800,
    inversionMensual: 2800,
    duracionMeses: 6,
    fechaInicio: '2026-05-01',
    fechaFin: '2026-11-01',
    metricas: { impresiones: 115000, clicks: 17000, leads: 1450, alcance: 290000, ctr: 14.8, conversionRate: 8.5, periodo: 'May-Nov 2026' },
    beneficios: ['Mención en programa gastronómico', 'Cena maridaje para oyentes', 'Evento de cocina en vivo'],
    categoriaRadio: 'Pop Latino',
    tags: ['mariscos', 'gastronomia', 'san-jose', 'pescado', 'restaurante'],
  },
  {
    id: 26,
    nombreNegocio: 'Lavandería Burbujas',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20laundry%20service%20brand%20logo%20with%20abstract%20water%20drop%20and%20soap%20bubble%20geometric%20design%2C%20fresh%20sky%20blue%20and%20white%20tones%2C%20modern%20clean%20service%20style%2C%20dark%20background%2C%20fresh%20simple%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-26&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Bright%20clean%20laundromat%20interior%20with%20modern%20washing%20machines%20and%20folding%20stations%2C%20natural%20light%2C%20organized%20professional%20environment%2C%20friendly%20neighborhood%20service%20atmosphere%2C%20editorial%20commercial%20photography%20with%20fresh%20clean%20tones%20and%20crisp%20composition&width=800&height=450&seq=pat-local-img-26&orientation=landscape',
    ciudad: 'Ciudad de México',
    estado: 'CDMX',
    pais: 'México',
    direccion: 'Calle Coahuila 120, Col. Roma Sur',
    descripcion: 'Lavandería con servicio de lavado, secado y planchado. Recogida y entrega a domicilio sin costo extra. Servicio express 24 horas.',
    sector: 'Servicios de Limpieza',
    contactoNombre: 'Patricia López',
    contactoEmail: 'patricia@lavanderiaburbujas.mx',
    contactoTelefono: '+52 55 8901 2345',
    website: 'https://lavanderiaburbujas.mx',
    fechaRegistro: '2026-05-01',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '104.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20mexican%20folk%20art%20inspired%20geometric%20pattern%2C%20warm%20terracotta%20and%20gold%20tones%2C%20circular%20emblem%20style%2C%20dark%20background%2C%20vibrant%20cultural%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-mexicana&orientation=squarish',
    displayEn: ['radio'],
    estado: 'pendiente',
    inversionTotal: 7200,
    inversionMensual: 1200,
    duracionMeses: 6,
    fechaInicio: '2026-07-01',
    fechaFin: '2026-12-31',
    metricas: { impresiones: 0, clicks: 0, leads: 0, alcance: 0, ctr: 0, conversionRate: 0, periodo: 'Pendiente' },
    beneficios: ['Mención en programa matutino', 'Descuento primer servicio'],
    categoriaRadio: 'Regional Mexicana',
    tags: ['lavanderia', 'limpieza', 'cdmx', 'servicio', 'local'],
  },
  {
    id: 27,
    nombreNegocio: 'Vivero Jardín Verde',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20plant%20nursery%20brand%20logo%20with%20abstract%20leaf%20and%20watering%20can%20geometric%20design%2C%20fresh%20green%20and%20earth%20tones%2C%20modern%20botanical%20style%2C%20dark%20background%2C%20natural%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-27&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Beautiful%20outdoor%20plant%20nursery%20with%20rows%20of%20green%20plants%20and%20flowers%2C%20greenhouse%20structure%2C%20natural%20sunlight%2C%20variety%20of%20potted%20plants%2C%20peaceful%20garden%20atmosphere%2C%20editorial%20photography%20with%20vibrant%20green%20tones%20and%20organic%20composition&width=800&height=450&seq=pat-local-img-27&orientation=landscape',
    ciudad: 'Bogotá',
    estado: 'Cundinamarca',
    pais: 'Colombia',
    direccion: 'Calle 63 #20-45, Chapinero',
    descripcion: 'Vivero urbano con plantas de interior y exterior, suculentas, árboles frutales y asesoría en jardinería. Talleres de huerto urbano.',
    sector: 'Jardinería y Plantas',
    contactoNombre: 'Catalina Herrera',
    contactoEmail: 'catalina@jardinverde.co',
    contactoTelefono: '+57 601 567 8901',
    website: 'https://jardinverde.co',
    fechaRegistro: '2026-04-15',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '96.0 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20building%20and%20antenna%20geometric%20design%2C%20warm%20amber%20and%20steel%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria-bog&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 9600,
    inversionMensual: 1600,
    duracionMeses: 6,
    fechaInicio: '2026-05-01',
    fechaFin: '2026-11-01',
    metricas: { impresiones: 58000, clicks: 8200, leads: 680, alcance: 150000, ctr: 14.1, conversionRate: 8.3, periodo: 'May-Nov 2026' },
    beneficios: ['Tips de jardinería al aire', 'Sorteo de plantas mensual', 'Taller gratuito para oyentes'],
    categoriaRadio: 'Noticias',
    tags: ['vivero', 'plantas', 'bogota', 'jardineria', 'local'],
  },
  {
    id: 28,
    nombreNegocio: 'Academia de Baile Ritmo Latino',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20dance%20academy%20brand%20logo%20with%20abstract%20dancing%20couple%20silhouette%20geometric%20design%2C%20passionate%20red%20and%20gold%20tones%2C%20modern%20latin%20dance%20style%2C%20dark%20background%2C%20energetic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-28&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Spacious%20dance%20studio%20with%20hardwood%20floors%20and%20mirrored%20walls%2C%20latin%20dance%20class%20in%20session%2C%20warm%20lighting%2C%20energetic%20atmosphere%2C%20couple%20dancing%20salsa%2C%20editorial%20photography%20with%20dynamic%20motion%20and%20warm%20passionate%20tones&width=800&height=450&seq=pat-local-img-28&orientation=landscape',
    ciudad: 'Madrid',
    estado: 'Comunidad de Madrid',
    pais: 'España',
    direccion: 'Calle Atocha 95, Lavapiés',
    descripcion: 'Academia de baile especializada en ritmos latinos: salsa, bachata, merengue y tango. Clases para todos los niveles. Fiestas sociales mensuales.',
    sector: 'Arte y Entretenimiento',
    contactoNombre: 'Prof. Alejandro Vargas',
    contactoEmail: 'alejandro@ritmolatino.es',
    contactoTelefono: '+34 91 567 8901',
    website: 'https://ritmolatino.es',
    fechaRegistro: '2026-05-01',
    radioNombre: 'Estéreo Capital',
    radioFrecuencia: '102.1 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20skyline%20and%20music%20note%20geometric%20design%2C%20sophisticated%20rose%20and%20charcoal%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-capital&orientation=squarish',
    displayEn: ['radio', 'programa', 'evento'],
    estado: 'activo',
    inversionTotal: 15000,
    inversionMensual: 2500,
    duracionMeses: 6,
    fechaInicio: '2026-06-01',
    fechaFin: '2026-12-01',
    metricas: { impresiones: 86000, clicks: 14000, leads: 1200, alcance: 230000, ctr: 16.3, conversionRate: 8.6, periodo: 'Jun-Dic 2026' },
    beneficios: ['Programa semanal Ritmo Latino', 'Fiesta de baile mensual para oyentes', 'Clase de prueba gratuita'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['baile', 'salsa', 'madrid', 'academia', 'servicio'],
  },
  {
    id: 29,
    nombreNegocio: 'Peluquería Canina Patitas',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20pet%20grooming%20brand%20logo%20with%20abstract%20dog%20paw%20and%20scissors%20geometric%20design%2C%20warm%20amber%20and%20cream%20tones%2C%20modern%20pet%20care%20style%2C%20dark%20background%2C%20friendly%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-29&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Clean%20modern%20pet%20grooming%20salon%20interior%20with%20grooming%20tables%20and%20bathing%20stations%2C%20happy%20dogs%20being%20groomed%2C%20organized%20professional%20environment%2C%20natural%20light%2C%20friendly%20atmosphere%2C%20editorial%20commercial%20photography%20with%20warm%20inviting%20tones&width=800&height=450&seq=pat-local-img-29&orientation=landscape',
    ciudad: 'Buenos Aires',
    estado: 'CABA',
    pais: 'Argentina',
    direccion: 'Av. Córdoba 2300, Recoleta',
    descripcion: 'Peluquería canina premium con productos hipoalergénicos. Corte, baño, tratamiento de pelo y spa para mascotas. Atención sin jaulas.',
    sector: 'Mascotas',
    contactoNombre: 'Gabriela Suárez',
    contactoEmail: 'gabriela@patitas.com.ar',
    contactoTelefono: '+54 11 7890 1234',
    website: 'https://patitas.com.ar',
    fechaRegistro: '2026-05-15',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '103.2 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20flame%20and%20rhythm%20wave%20geometric%20shapes%2C%20warm%20coral%20and%20midnight%20blue%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20passionate%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito-ba&orientation=squarish',
    displayEn: ['radio'],
    estado: 'activo',
    inversionTotal: 6000,
    inversionMensual: 1000,
    duracionMeses: 6,
    fechaInicio: '2026-06-01',
    fechaFin: '2026-12-01',
    metricas: { impresiones: 38000, clicks: 5200, leads: 450, alcance: 100000, ctr: 13.7, conversionRate: 8.7, periodo: 'Jun-Dic 2026' },
    beneficios: ['Mención en programa de la tarde', 'Descuento primera visita'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['mascotas', 'peluqueria-canina', 'buenos-aires', 'perros', 'servicio'],
  },
  {
    id: 30,
    nombreNegocio: 'Feria Gastronómica Sabores',
    tipo: 'evento',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20food%20festival%20brand%20logo%20with%20abstract%20fork%20and%20plate%20geometric%20design%2C%20warm%20terracotta%20and%20olive%20green%20tones%2C%20modern%20culinary%20event%20style%2C%20dark%20background%2C%20appetizing%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-30&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20outdoor%20food%20festival%20with%20multiple%20food%20trucks%20and%20tasting%20stations%2C%20string%20lights%20overhead%2C%20families%20enjoying%20diverse%20cuisines%2C%20summer%20evening%20atmosphere%2C%20editorial%20event%20photography%20with%20warm%20festive%20tones%20and%20lively%20composition&width=800&height=450&seq=pat-local-img-30&orientation=landscape',
    ciudad: 'Guadalajara',
    estado: 'Jalisco',
    pais: 'México',
    direccion: 'Parque Metropolitano, Zapopan',
    descripcion: 'Festival gastronómico anual que reúne a los mejores restaurantes y chefs de Jalisco. Degustaciones, clases de cocina y concurso del mejor platillo.',
    sector: 'Gastronomía y Eventos',
    contactoNombre: 'Comité Gastronómico GDL',
    contactoEmail: 'info@feriasabores.mx',
    contactoTelefono: '+52 33 8901 2345',
    website: 'https://feriasabores.mx',
    fechaRegistro: '2026-06-01',
    radioNombre: 'Radio Deportiva Total',
    radioFrecuencia: '95.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20sports%20radio%20station%20logo%20with%20abstract%20trophy%20and%20ball%20geometric%20design%2C%20energetic%20red%20and%20black%20tones%2C%20bold%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20competitive%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-deportiva&orientation=squarish',
    displayEn: ['radio', 'evento', 'programa'],
    estado: 'activo',
    inversionTotal: 15000,
    inversionMensual: 5000,
    duracionMeses: 3,
    fechaInicio: '2026-07-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 92000, clicks: 16800, leads: 2400, alcance: 260000, ctr: 18.3, conversionRate: 14.3, periodo: 'Jul-Oct 2026' },
    beneficios: ['Cobertura en vivo del festival', 'Entrevistas con chefs participantes', 'Sorteo de pases VIP'],
    categoriaRadio: 'Deportiva',
    tags: ['feria', 'gastronomia', 'guadalajara', 'festival', 'evento'],
  },
  {
    id: 31,
    nombreNegocio: 'Imprenta Gráfica Express',
    tipo: 'empresa',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20printing%20company%20brand%20logo%20with%20abstract%20printing%20press%20and%20paper%20roll%20geometric%20design%2C%20cyan%20blue%20and%20magenta%20tones%2C%20modern%20print%20industry%20style%2C%20dark%20background%2C%20precise%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-31&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Modern%20printing%20facility%20with%20industrial%20printing%20presses%20in%20operation%2C%20organized%20paper%20stock%2C%20bright%20industrial%20lighting%2C%20professional%20print%20production%20environment%2C%20editorial%20commercial%20photography%20with%20crisp%20lighting%20and%20technical%20composition&width=800&height=450&seq=pat-local-img-31&orientation=landscape',
    ciudad: 'Monterrey',
    estado: 'Nuevo León',
    pais: 'México',
    direccion: 'Av. Revolución 890, Centro',
    descripcion: 'Imprenta digital y offset con servicio express. Tarjetas, volantes, lonas, catálogos y material POP. Diseño gráfico incluido sin costo.',
    sector: 'Impresión y Publicidad',
    contactoNombre: 'Lic. Mario Cantú',
    contactoEmail: 'mario@graficaexpress.mx',
    contactoTelefono: '+52 81 9012 3456',
    website: 'https://graficaexpress.mx',
    fechaRegistro: '2026-03-10',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '93.5 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20industrial%20radio%20station%20logo%20with%20abstract%20factory%20gear%20and%20antenna%20geometric%20design%2C%20steel%20blue%20and%20copper%20tones%2C%20bold%20modern%20circular%20emblem%2C%20dark%20background%2C%20strong%20industrial%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria&orientation=squarish',
    displayEn: ['radio', 'club'],
    estado: 'activo',
    inversionTotal: 21000,
    inversionMensual: 3500,
    duracionMeses: 6,
    fechaInicio: '2026-04-01',
    fechaFin: '2026-10-01',
    metricas: { impresiones: 155000, clicks: 21500, leads: 1600, alcance: 370000, ctr: 13.9, conversionRate: 7.4, periodo: 'Abr-Oct 2026' },
    beneficios: ['Descuentos para miembros del club', 'Material impreso para eventos de la radio'],
    categoriaRadio: 'Noticias y Negocios',
    tags: ['imprenta', 'publicidad', 'monterrey', 'impresion', 'empresa'],
  },
  {
    id: 32,
    nombreNegocio: 'Heladería Dolce Vita',
    tipo: 'restaurante',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20gelato%20ice%20cream%20shop%20brand%20logo%20with%20abstract%20ice%20cream%20scoop%20and%20cone%20geometric%20design%2C%20pastel%20pink%20and%20mint%20green%20tones%2C%20modern%20sweet%20shop%20style%2C%20dark%20background%2C%20delightful%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-32&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Charming%20artisanal%20gelato%20shop%20interior%20with%20colorful%20gelato%20displays%20in%20glass%20case%2C%20pastel%20interior%20design%2C%20natural%20light%2C%20happy%20customers%2C%20Italian%20gelato%20atmosphere%2C%20editorial%20food%20photography%20with%20soft%20pastel%20tones%20and%20inviting%20composition&width=800&height=450&seq=pat-local-img-32&orientation=landscape',
    ciudad: 'Buenos Aires',
    estado: 'CABA',
    pais: 'Argentina',
    direccion: 'Av. del Libertador 4500, Belgrano',
    descripcion: 'Heladería artesanal con recetas italianas y sabores argentinos. 48 gustos rotativos, materia prima importada de Italia. Opciones veganas.',
    sector: 'Gastronomía',
    contactoNombre: 'Lucía Bianchi',
    contactoEmail: 'lucia@dolcevita.com.ar',
    contactoTelefono: '+54 11 8901 2345',
    website: 'https://dolcevita.com.ar',
    fechaRegistro: '2026-05-20',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '103.2 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20flame%20and%20rhythm%20wave%20geometric%20shapes%2C%20warm%20coral%20and%20midnight%20blue%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20passionate%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito-ba&orientation=squarish',
    displayEn: ['radio', 'concurso'],
    estado: 'activo',
    inversionTotal: 10800,
    inversionMensual: 1800,
    duracionMeses: 6,
    fechaInicio: '2026-06-01',
    fechaFin: '2026-12-01',
    metricas: { impresiones: 72000, clicks: 11000, leads: 900, alcance: 190000, ctr: 15.3, conversionRate: 8.2, periodo: 'Jun-Dic 2026' },
    beneficios: ['Mención en programa de la tarde', 'Concurso Verano Dulce', '2x1 para oyentes los lunes'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['heladeria', 'gelato', 'buenos-aires', 'postres', 'restaurante'],
  },
  {
    id: 33,
    nombreNegocio: 'Agencia de Marketing Creactivo',
    tipo: 'empresa',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20marketing%20agency%20brand%20logo%20with%20abstract%20lightbulb%20and%20target%20geometric%20design%2C%20vibrant%20coral%20and%20deep%20purple%20tones%2C%20modern%20creative%20agency%20style%2C%20dark%20background%2C%20innovative%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-33&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Modern%20creative%20agency%20office%20with%20open%20plan%20workspace%20and%20brainstorming%20areas%2C%20colorful%20design%20elements%2C%20natural%20light%2C%20collaborative%20creative%20atmosphere%2C%20editorial%20interior%20photography%20with%20bright%20inspiring%20tones%20and%20creative%20composition&width=800&height=450&seq=pat-local-img-33&orientation=landscape',
    ciudad: 'Bogotá',
    estado: 'Cundinamarca',
    pais: 'Colombia',
    direccion: 'Calle 85 #12-30, Zona Rosa',
    descripcion: 'Agencia de marketing digital y branding. Especialistas en redes sociales, SEO, diseño web y producción audiovisual. Clientes en 5 países.',
    sector: 'Marketing y Publicidad',
    contactoNombre: 'Andrea Castillo',
    contactoEmail: 'andrea@creactivo.co',
    contactoTelefono: '+57 601 678 9012',
    website: 'https://creactivo.co',
    fechaRegistro: '2026-02-01',
    radioNombre: 'Radio Industria',
    radioFrecuencia: '96.0 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20with%20abstract%20city%20building%20and%20antenna%20geometric%20design%2C%20warm%20amber%20and%20steel%20tones%2C%20urban%20modern%20circular%20emblem%2C%20dark%20background%2C%20metropolitan%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-industria-bog&orientation=squarish',
    displayEn: ['radio', 'programa', 'concurso'],
    estado: 'activo',
    inversionTotal: 30000,
    inversionMensual: 5000,
    duracionMeses: 6,
    fechaInicio: '2026-03-01',
    fechaFin: '2026-09-01',
    metricas: { impresiones: 220000, clicks: 33000, leads: 2600, alcance: 520000, ctr: 15.0, conversionRate: 7.9, periodo: 'Mar-Sep 2026' },
    beneficios: ['Segmento de marketing semanal', 'Concurso de branding para emprendedores', 'Auditoría digital gratuita para oyentes'],
    categoriaRadio: 'Noticias',
    tags: ['marketing', 'publicidad', 'bogota', 'digital', 'empresa'],
  },
  {
    id: 34,
    nombreNegocio: 'Taller de Bicicletas Pedalea',
    tipo: 'negocio-local',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20bicycle%20repair%20shop%20brand%20logo%20with%20abstract%20bicycle%20wheel%20and%20chain%20geometric%20design%2C%20vibrant%20green%20and%20charcoal%20tones%2C%20modern%20cycling%20style%2C%20dark%20background%2C%20dynamic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-34&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Organized%20bicycle%20repair%20workshop%20with%20bikes%20on%20stands%20and%20tools%20displayed%20on%20pegboard%2C%20natural%20light%2C%20clean%20professional%20mechanic%20environment%2C%20cycling%20culture%20atmosphere%2C%20editorial%20commercial%20photography%20with%20bright%20tones%20and%20industrial%20charm&width=800&height=450&seq=pat-local-img-34&orientation=landscape',
    ciudad: 'Ciudad de México',
    estado: 'CDMX',
    pais: 'México',
    direccion: 'Av. Universidad 1800, Col. Narvarte',
    descripcion: 'Taller de bicicletas urbano con servicio completo de reparación, venta de refacciones y bicis nuevas. Organizador de rodadas dominicales.',
    sector: 'Deportes y Movilidad',
    contactoNombre: 'Diego Hernández',
    contactoEmail: 'diego@pedalea.mx',
    contactoTelefono: '+52 55 9012 3456',
    website: 'https://pedalea.mx',
    fechaRegistro: '2026-06-01',
    radioNombre: 'La Mexicana FM',
    radioFrecuencia: '104.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20mexican%20folk%20art%20inspired%20geometric%20pattern%2C%20warm%20terracotta%20and%20gold%20tones%2C%20circular%20emblem%20style%2C%20dark%20background%2C%20vibrant%20cultural%20identity%2C%20clean%20modern%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-mexicana&orientation=squarish',
    displayEn: ['radio'],
    estado: 'pendiente',
    inversionTotal: 6000,
    inversionMensual: 1500,
    duracionMeses: 4,
    fechaInicio: '2026-08-01',
    fechaFin: '2026-12-01',
    metricas: { impresiones: 0, clicks: 0, leads: 0, alcance: 0, ctr: 0, conversionRate: 0, periodo: 'Pendiente' },
    beneficios: ['Mención en programa deportivo', 'Ajuste gratuito para oyentes'],
    categoriaRadio: 'Regional Mexicana',
    tags: ['bicicletas', 'taller', 'cdmx', 'movilidad', 'local'],
  },
  {
    id: 35,
    nombreNegocio: 'Estética Belleza Total',
    tipo: 'servicio',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20beauty%20salon%20brand%20logo%20with%20abstract%20flower%20and%20mirror%20geometric%20design%2C%20elegant%20rose%20gold%20and%20cream%20tones%2C%20modern%20beauty%20spa%20style%2C%20dark%20background%2C%20sophisticated%20feminine%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-35&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Luxurious%20beauty%20salon%20interior%20with%20styling%20stations%20and%20treatment%20rooms%2C%20elegant%20decor%20with%20gold%20accents%2C%20soft%20ambient%20lighting%2C%20professional%20beauty%20environment%2C%20editorial%20interior%20photography%20with%20sophisticated%20warm%20tones%20and%20refined%20composition&width=800&height=450&seq=pat-local-img-35&orientation=landscape',
    ciudad: 'Guadalajara',
    estado: 'Jalisco',
    pais: 'México',
    direccion: 'Av. Patria 1500, Col. Jardines',
    descripcion: 'Salón de belleza integral: corte, color, tratamientos capilares, manicure, pedicure, faciales y masajes. Productos profesionales importados.',
    sector: 'Belleza y Estética',
    contactoNombre: 'Karla Mendoza',
    contactoEmail: 'karla@bellezatotal.mx',
    contactoTelefono: '+52 33 0123 4567',
    website: 'https://bellezatotal.mx',
    fechaRegistro: '2026-06-15',
    radioNombre: 'Radio Éxito FM',
    radioFrecuencia: '98.5 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20radio%20station%20logo%20design%20with%20abstract%20star%20and%20signal%20wave%20geometric%20shapes%2C%20energetic%20coral%20and%20gold%20tones%2C%20modern%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20vibrant%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-exito&orientation=squarish',
    displayEn: ['radio', 'programa'],
    estado: 'activo',
    inversionTotal: 14400,
    inversionMensual: 2400,
    duracionMeses: 6,
    fechaInicio: '2026-07-01',
    fechaFin: '2026-12-31',
    metricas: { impresiones: 45000, clicks: 6800, leads: 580, alcance: 130000, ctr: 15.1, conversionRate: 8.5, periodo: 'Jul-Dic 2026' },
    beneficios: ['Tips de belleza semanal', 'Descuento del 20% para oyentes'],
    categoriaRadio: 'Pop y Éxitos',
    tags: ['estetica', 'belleza', 'guadalajara', 'salon', 'servicio'],
  },
  {
    id: 36,
    nombreNegocio: 'Evento Maratón de la Ciudad',
    tipo: 'evento',
    logoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20marathon%20event%20brand%20logo%20with%20abstract%20running%20figure%20and%20finish%20line%20geometric%20design%2C%20energetic%20orange%20and%20white%20tones%2C%20modern%20sports%20event%20style%2C%20dark%20background%2C%20dynamic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=pat-local-36&orientation=landscape',
    imagenUrl: 'https://readdy.ai/api/search-image?query=Large%20city%20marathon%20event%20with%20runners%20crossing%20finish%20line%2C%20cheering%20crowds%2C%20urban%20skyline%20background%2C%20morning%20sunlight%2C%20inspiring%20athletic%20atmosphere%2C%20editorial%20sports%20photography%20with%20dynamic%20energy%20and%20celebratory%20composition&width=800&height=450&seq=pat-local-img-36&orientation=landscape',
    ciudad: 'Monterrey',
    estado: 'Nuevo León',
    pais: 'México',
    direccion: 'Macroplaza, Centro de Monterrey',
    descripcion: 'Maratón anual de la ciudad con categorías 5K, 10K, 21K y 42K. Certificado internacional. 15,000 corredores esperados.',
    sector: 'Deportes y Eventos',
    contactoNombre: 'Comité Maratón MTY',
    contactoEmail: 'info@maratonmty.mx',
    contactoTelefono: '+52 81 0123 4567',
    website: 'https://maratonmty.mx',
    fechaRegistro: '2026-06-20',
    radioNombre: 'Radio Deportiva Total',
    radioFrecuencia: '95.7 FM',
    radioLogoUrl: 'https://readdy.ai/api/search-image?query=Minimalist%20sports%20radio%20station%20logo%20with%20abstract%20trophy%20and%20ball%20geometric%20design%2C%20energetic%20red%20and%20black%20tones%2C%20bold%20dynamic%20circular%20emblem%2C%20dark%20background%2C%20competitive%20identity%2C%20clean%20shapes%2C%20no%20text&width=160&height=160&seq=radio-logo-deportiva&orientation=squarish',
    displayEn: ['radio', 'evento', 'programa', 'concurso'],
    estado: 'activo',
    inversionTotal: 24000,
    inversionMensual: 8000,
    duracionMeses: 3,
    fechaInicio: '2026-08-01',
    fechaFin: '2026-11-01',
    metricas: { impresiones: 78000, clicks: 15000, leads: 2800, alcance: 210000, ctr: 19.2, conversionRate: 18.7, periodo: 'Ago-Nov 2026' },
    beneficios: ['Cobertura total del evento', 'Programa de entrenamiento semanal', 'Sorteo de inscripciones gratuitas', 'Entrevistas con atletas élite'],
    categoriaRadio: 'Deportiva',
    tags: ['maraton', 'deporte', 'monterrey', 'carrera', 'evento'],
  },
];

export const metricasPorDisplay: { display: DisplayPatrocinadorLocal; label: string; icono: string; totalPatrocinios: number; inversionTotal: number; impresionesTotales: number; clicksTotales: number; leadsTotales: number; }[] = [
  { display: 'radio', label: 'Radio', icono: 'ri-radio-line', totalPatrocinios: 22, inversionTotal: 520000, impresionesTotales: 3200000, clicksTotales: 485000, leadsTotales: 52000 },
  { display: 'programa', label: 'Programa', icono: 'ri-mic-line', totalPatrocinios: 18, inversionTotal: 380000, impresionesTotales: 2500000, clicksTotales: 380000, leadsTotales: 42000 },
  { display: 'evento', label: 'Evento', icono: 'ri-calendar-event-line', totalPatrocinios: 12, inversionTotal: 180000, impresionesTotales: 950000, clicksTotales: 155000, leadsTotales: 18000 },
  { display: 'concurso', label: 'Concurso', icono: 'ri-trophy-line', totalPatrocinios: 10, inversionTotal: 145000, impresionesTotales: 780000, clicksTotales: 128000, leadsTotales: 15000 },
  { display: 'club', label: 'Club', icono: 'ri-group-line', totalPatrocinios: 8, inversionTotal: 95000, impresionesTotales: 520000, clicksTotales: 82000, leadsTotales: 9500 },
];

export function getPatrocinadoresPorTipo(tipo: TipoPatrocinadorLocal | 'todos'): PatrocinadorLocal[] {
  if (tipo === 'todos') return patrocinadoresLocales;
  return patrocinadoresLocales.filter(p => p.tipo === tipo);
}

export function getPatrocinadoresPorDisplay(display: DisplayPatrocinadorLocal | 'todos'): PatrocinadorLocal[] {
  if (display === 'todos') return patrocinadoresLocales;
  return patrocinadoresLocales.filter(p => p.displayEn.includes(display));
}

export function getPatrocinadoresPorEstado(estado: EstadoPatrocinio | 'todos'): PatrocinadorLocal[] {
  if (estado === 'todos') return patrocinadoresLocales;
  return patrocinadoresLocales.filter(p => p.estado === estado);
}

export function getPatrocinadoresPorCiudad(ciudad: string): PatrocinadorLocal[] {
  return patrocinadoresLocales.filter(p => p.ciudad.toLowerCase().includes(ciudad.toLowerCase()));
}

export function getPatrocinadoresActivos(): PatrocinadorLocal[] {
  return patrocinadoresLocales.filter(p => p.estado === 'activo');
}

export function getTopPatrocinadoresPorInversion(limit: number = 10): PatrocinadorLocal[] {
  return [...patrocinadoresLocales].sort((a, b) => b.inversionTotal - a.inversionTotal).slice(0, limit);
}

export function getTopPatrocinadoresPorRendimiento(limit: number = 10): PatrocinadorLocal[] {
  return [...patrocinadoresLocales]
    .filter(p => p.metricas.ctr > 0)
    .sort((a, b) => b.metricas.ctr - a.metricas.ctr)
    .slice(0, limit);
}

export function getPatrocinadoresLocalesPorRadio(radioFrecuencia: string): PatrocinadorLocal[] {
  return patrocinadoresLocales.filter(p => p.radioFrecuencia === radioFrecuencia);
}

export function formatearDinero(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${n}`;
}

export function formatearNumero(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}